import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { centerToast } from '../components/CenterToast';
import { getShareLink, todayDDMM, shortId } from '../utils/links';
import MorphButton from '../components/MorphButton';
import { requireAuth } from '../components/AuthGate';
import { ArrowLeft, Home, Hospital, Package, CheckCircle, Star, Clock, MessageCircle, Link2, Users, Copy } from 'lucide-react';

const HOSPITALS = [
  'Singapore General Hospital',
  'Tan Tock Seng Hospital',
  'Changi General Hospital',
  'KK Women\'s and Children\'s Hospital',
  'National University Hospital',
  'Ng Teng Fong General Hospital'
];

const NEA_FEE = 315;

const PACKAGES = [
  {
    id: 'essential',
    name: 'Essential Burial Package',
    priceMin: 1200,
    priceMax: 1300,
    neaFee: NEA_FEE,
    eta: '1 hour',
    rating: 4.8,
    mostCommon: false,
    target: 'Families seeking simple, direct-to-burial arrangements without large crowds.',
    features: [
      'Hearse transport from home/hospital to mosque and Pusara Aman cemetery',
      'Certified Ghusl (ritual washing) and Kafan (shrouding)',
      'Imam to lead Janazah prayers and recite Talqin',
      'Standard wooden burial planks and basic headstone (Kayu Nisan)',
      'NEA documentation and burial permit assistance'
    ]
  },
  {
    id: 'standard',
    name: 'Standard Complete Package',
    priceMin: 1450,
    priceMax: 1600,
    neaFee: NEA_FEE,
    eta: '45 mins',
    rating: 4.9,
    mostCommon: true,
    target: 'The typical choice for most local families, accommodating extended family and friends.',
    features: [
      'All Essential Burial services included',
      '40–50 seater air-conditioned bus (home/hospital ↔ mosque ↔ cemetery)',
      'Hospital-to-home transfer (if body brought home first)',
      'Ceramic name signboard for the deceased',
      'Temporary carpet grass over the grave site',
      'Basic refreshments (bottled water) for attendees'
    ]
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive Care Package',
    priceMin: 1900,
    priceMax: 2200,
    neaFee: NEA_FEE,
    eta: '30 mins',
    rating: 5.0,
    mostCommon: false,
    target: 'Families seeking added comfort for guests, post-burial support, or complex circumstances.',
    features: [
      'All Standard Complete services included',
      'Food & catering distribution (50+ packets) at home or mosque',
      'Upgraded grave landscaping + 6 months grave maintenance',
      'Coordination & setup for post-funeral Tahlil prayers',
      'Special handling (infectious diseases, decomposed bodies, specialised equipment)'
    ]
  }
];

export default function BookingWizard({ onComplete, onBack }) {
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [deceasedName, setDeceasedName] = useState('');
  const [warisName, setWarisName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [trackingLink, setTrackingLink] = useState('');

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const handleLocationSelect = (loc) => {
    setLocation(loc);
    if (loc === 'home') {
      setShowWarning(true);
    } else {
      setShowWarning(false);
      setTimeout(() => setStep(2), 300);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setTimeout(() => setStep(3), 300);
  };

  const isValidSGMobile = (num) => /^[89]\d{7}$/.test(num);

  const handleMobileChange = (e) => {
    // Strip non-digits, cap at 8 chars
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    setMobileNumber(digits);
  };

  const handleBookingDetails = () => {
    if (!deceasedName || !warisName || !mobileNumber || !pickupLocation) {
      toast.error('Please fill in all required fields', { position: 'top-center' });
      return;
    }
    if (!isValidSGMobile(mobileNumber)) {
      toast.error('Please enter a valid 8-digit Singapore mobile number starting with 8 or 9', { position: 'top-center' });
      return;
    }
    setStep(4);
  };

  const formatMobileDisplay = (num) => {
    if (!num) return '';
    const mid = num.slice(0, 4);
    const end = num.slice(4);
    return end ? `+65 ${mid} ${end}` : `+65 ${mid}`;
  };

  const handleConfirmBooking = () => {
    // Generate tracking link — funnelled through utils/links.js
    const pkgMap = { essential: 'pkg1', standard: 'pkg2', comprehensive: 'pkg3' };
    const pkgSlug = pkgMap[selectedPackage.id] || 'pkg1';
    const link = getShareLink('casket-track', {
      ddmm: todayDDMM(),
      pkg: pkgSlug,
      id: shortId()
    });
    setTrackingLink(link);

    const bookingData = {
      deceasedName,
      warisName,
      mobileNumber: `+65${mobileNumber}`,
      pickupLocation,
      package: selectedPackage,
      location,
      trackingLink: link
    };
    onComplete(bookingData);
    setStep(5);
  };

  const buildWhatsAppMessage = () =>
    `Assalamualaikum,\n\nWe are making arrangements for the late ${deceasedName || 'our beloved'} with Ihsan Casket. You can follow the Jenazah's movements, view funeral details, and make a digital donation here:\n\n${trackingLink}\n\nMay Allah grant patience and ease. Ameen.`;

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(buildWhatsAppMessage());
    // Opens WhatsApp share sheet (no recipient — lets user pick contacts/groups)
    const url = `https://wa.me/?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(trackingLink);
    centerToast('Tracking link copied to clipboard', '🔗');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold">Booking Service</h1>
            <div className="w-16"></div>
          </div>
          {step < 5 && (
            <div className="mt-4">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground mt-2 text-center">
                Step {step} of {totalSteps}
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Step 1: Triage */}
        {step === 1 && (
          <div className="page-transition space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3">Where is the body?</h2>
              <p className="text-muted-foreground text-lg">This helps us coordinate the right services</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
              <Card 
                className="cursor-pointer hover:border-primary hover:shadow-lg transition-all border-2"
                onClick={() => handleLocationSelect('hospital')}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Hospital className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Hospital</h3>
                  <p className="text-muted-foreground">Body is at a hospital facility</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-warning hover:shadow-lg transition-all border-2"
                onClick={() => handleLocationSelect('home')}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Home className="h-8 w-8 text-warning" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Home</h3>
                  <p className="text-muted-foreground">Body is at home residence</p>
                </CardContent>
              </Card>
            </div>

            {showWarning && (
              <Alert className="max-w-2xl mx-auto border-warning bg-warning/10 animate-pulse-gentle">
                <AlertDescription className="text-center text-base sm:text-lg">
                  ⚠️ Please call a doctor first for certification before proceeding.
                  <div className="mt-4">
                    <Button 
                      onClick={() => {
                        setShowWarning(false);
                        setStep(2);
                      }}
                      className="w-full sm:w-auto"
                    >
                      Doctor Called - Continue
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Step 2: Package Selection */}
        {step === 2 && (
          <div className="page-transition space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3">Select a Service Package</h2>
              <p className="text-muted-foreground text-lg">Choose the package that best meets your needs</p>
            </div>

            <div className="grid gap-6 max-w-3xl mx-auto">
              {PACKAGES.map((pkg) => (
                <Card 
                  key={pkg.id}
                  data-testid={`package-card-${pkg.id}`}
                  className={`cursor-pointer hover:border-primary hover:shadow-xl transition-all border-2 relative ${pkg.mostCommon ? 'border-primary shadow-lg' : ''}`}
                  onClick={() => handlePackageSelect(pkg)}
                >
                  {pkg.mostCommon && (
                    <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full shadow">
                      Most Common
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Package className="h-6 w-6 text-primary" />
                          <h3 className="text-xl sm:text-2xl font-semibold">{pkg.name}</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mb-3">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">ETA: {pkg.eta}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{pkg.rating}</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground italic mb-3">
                          {pkg.target}
                        </p>
                        <ul className="space-y-1.5">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <CheckCircle className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-center sm:text-right sm:min-w-[160px]">
                        <div className="text-2xl sm:text-3xl font-bold text-primary leading-tight">
                          From ${pkg.priceMin.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Estimated cost</p>
                        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                          Including NEA Burial Fee of ${pkg.neaFee}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Booking Details */}
        {step === 3 && selectedPackage && (
          <div className="page-transition space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3">Booking Details</h2>
              <p className="text-muted-foreground text-lg">Please provide the necessary information</p>
            </div>

            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="text-xl">Service Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="deceased-name" className="text-base">Deceased Name *</Label>
                  <Input
                    id="deceased-name"
                    data-testid="input-deceased-name"
                    placeholder="Enter full name"
                    value={deceasedName}
                    onChange={(e) => setDeceasedName(e.target.value)}
                    className="h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="waris-name" className="text-base">Waris Name (Next of Kin) *</Label>
                  <Input
                    id="waris-name"
                    data-testid="input-waris-name"
                    placeholder="Enter full name of person in charge of rites"
                    value={warisName}
                    onChange={(e) => setWarisName(e.target.value)}
                    className="h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile-number" className="text-base">Mobile Number (WhatsApp) *</Label>
                  <div
                    className={`flex items-center h-12 rounded-md border bg-background overflow-hidden transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${
                      mobileNumber && !isValidSGMobile(mobileNumber)
                        ? 'border-destructive'
                        : 'border-input'
                    }`}
                  >
                    <span
                      className="flex items-center gap-1 px-3 h-full bg-muted/60 border-r text-sm font-medium text-foreground select-none"
                      aria-label="Singapore country code"
                    >
                      <span role="img" aria-label="Singapore flag">🇸🇬</span>
                      +65
                    </span>
                    <Input
                      id="mobile-number"
                      data-testid="input-mobile-number"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      maxLength={8}
                      placeholder="8123 4567"
                      value={mobileNumber}
                      onChange={handleMobileChange}
                      className="h-full border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-base flex-1"
                    />
                  </div>
                  {mobileNumber && !isValidSGMobile(mobileNumber) ? (
                    <p
                      data-testid="mobile-number-error"
                      className="text-xs text-destructive leading-snug"
                    >
                      Enter a valid 8-digit Singapore mobile starting with 8 or 9.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-snug">
                      This will be our single source of communication and where we will send your tracking link.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup-location" className="text-base">Pickup Location *</Label>
                  <Select value={pickupLocation} onValueChange={setPickupLocation}>
                    <SelectTrigger className="h-12 text-base" data-testid="select-pickup-location">
                      <SelectValue placeholder="Select hospital" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOSPITALS.map((hospital) => (
                        <SelectItem key={hospital} value={hospital} className="text-base">
                          {hospital}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Selected Package:</span>
                    <span className="font-semibold">{selectedPackage.name}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-muted-foreground">Estimated Cost:</span>
                    <span className="text-2xl font-bold text-primary">
                      From ${selectedPackage.priceMin.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-right">
                    Including NEA Burial Fee of ${selectedPackage.neaFee}
                  </p>
                </div>

                {/* What happens next - reassurance card */}
                <div
                  data-testid="what-happens-next-card"
                  className="rounded-xl bg-primary/5 border border-primary/15 p-5 sm:p-6 space-y-4"
                >
                  <h3 className="text-base sm:text-lg font-semibold text-foreground">
                    What happens next?
                  </h3>

                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                        1
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center gap-2 mb-1">
                          <MessageCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <p className="font-semibold text-sm sm:text-base">WhatsApp Confirmation</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          An official Ihsan Casket representative will reach out to this mobile number shortly via WhatsApp to verify details and assist you.
                        </p>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                        2
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Link2 className="h-4 w-4 text-primary flex-shrink-0" />
                          <p className="font-semibold text-sm sm:text-base">Live Tracking Link</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          You will receive a secure live tracking link directly to your WhatsApp.
                        </p>
                        <code className="block mt-2 text-[11px] sm:text-xs bg-background border rounded-md px-2 py-1.5 text-muted-foreground break-all">
                          {getShareLink('casket-track', { ddmm: '2204', pkg: 'pkg2', id: 'fj5s7o5fkd4l' })}
                        </code>
                      </div>
                    </li>

                    <li className="flex gap-3">
                      <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                        3
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-4 w-4 text-primary flex-shrink-0" />
                          <p className="font-semibold text-sm sm:text-base">Share & Support</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          You can easily forward this link to family and friends. It allows them to track the Jenazah's movements in real-time, view funeral details, and make digital donations directly to the deceased.
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>

                <Button 
                  onClick={handleBookingDetails}
                  data-testid="btn-continue-confirmation"
                  className="w-full h-12 text-base"
                  size="lg"
                >
                  Continue to Confirmation
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && selectedPackage && (
          <div className="page-transition space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3">Confirm Your Booking</h2>
              <p className="text-muted-foreground text-lg">Please review the details before confirming</p>
            </div>

            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="text-xl">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Deceased Name:</span>
                    <span className="font-medium">{deceasedName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Waris (Next of Kin):</span>
                    <span className="font-medium">{warisName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">WhatsApp:</span>
                    <span className="font-medium">{formatMobileDisplay(mobileNumber)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Pickup Location:</span>
                    <span className="font-medium text-right">{pickupLocation}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Package:</span>
                    <span className="font-medium">{selectedPackage.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Estimated Arrival:</span>
                    <span className="font-medium">{selectedPackage.eta}</span>
                  </div>
                  <div className="flex justify-between py-3 bg-muted/50 px-4 rounded-lg mt-4">
                    <span className="font-semibold text-lg">Estimated Cost:</span>
                    <span className="text-2xl font-bold text-primary">
                      From ${selectedPackage.priceMin.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-right px-4">
                    Including NEA Burial Fee of ${selectedPackage.neaFee}
                  </p>
                </div>

                <MorphButton
                  as={Button}
                  data-testid="btn-confirm-booking"
                  idleLabel="Confirm Booking"
                  successLabel="Confirmed"
                  onClick={requireAuth}
                  onComplete={handleConfirmBooking}
                  className="w-full h-12 text-base"
                  size="lg"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="page-transition space-y-6">
            <Card className="max-w-2xl mx-auto text-center">
              <CardContent className="p-8 sm:p-12">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-12 w-12 text-primary" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold mb-4">Booking Confirmed</h2>
                <p className="text-base sm:text-lg text-muted-foreground mb-6">
                  Your service has been scheduled. We will arrive at {pickupLocation} in approximately {selectedPackage.eta}.
                </p>

                <div className="bg-primary/5 border border-primary/15 p-5 rounded-xl mb-6 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="h-4 w-4 text-primary flex-shrink-0" />
                    <p className="text-sm font-semibold">
                      We've sent this tracking link to {formatMobileDisplay(mobileNumber)}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                    Forward it to family & friends so they can follow the Jenazah live and make digital donations.
                  </p>
                  <code
                    data-testid="tracking-link"
                    className="text-xs sm:text-sm bg-background px-3 py-2 rounded border block break-all text-muted-foreground"
                  >
                    {trackingLink}
                  </code>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    data-testid="btn-share-whatsapp"
                    onClick={handleShareWhatsApp}
                    className="h-12 text-base gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white"
                    size="lg"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Share via WhatsApp
                  </Button>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      data-testid="btn-copy-link"
                      onClick={handleCopyLink}
                      variant="outline"
                      className="flex-1 h-12 text-base gap-2"
                      size="lg"
                    >
                      <Copy className="h-4 w-4" />
                      Copy Link
                    </Button>
                    <Button
                      onClick={onBack}
                      variant="outline"
                      className="flex-1 h-12 text-base"
                      size="lg"
                    >
                      Return Home
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
